import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronDown,
  Check,
  Search,
  FileText,
  Monitor,
  Rocket,
  Wrench,
  Sparkles,
  Clock,
  CircleDollarSign,
  MessageSquare,
  Milestone,
  Loader2,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { usePackageOfferings } from "@/providers/ContentProvider";
import {
  ProjectBasicsData,
  INDUSTRIES,
  COMPANY_SIZES,
  TIMEZONES,
  isRecurringPackage,
} from "@/domain/intake";
import { cn } from "@/lib/utils";
import type { PackageOfferingDto } from "@shared/types/PackageOfferingDto";
import { useToast } from "@/hooks/use-toast";
import {
  useEmailDomainValidation,
  EmailDomainStatus,
} from "@/hooks/use-email-domain-validation";
import { CloudflareTurnstile } from "@/components/auth/CloudflareTurnstile";

function getIconForPackage(id: string) {
  if (id === "pkg-concept-to-blueprint") return FileText;
  if (id === "pkg-mvp-rescue-audit") return Search;
  if (id === "pkg-desktop-feasibility") return Monitor;
  if (id === "pkg-ai-desktop-launch") return Rocket;
  if (id === "pkg-shipping-ops-partner") return Wrench;
  return Sparkles;
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function PackageMilestoneBreakdown({
  offering,
  onRequestCustomQuote,
  selectedTier,
  onTierSelect,
}: {
  offering: PackageOfferingDto;
  onRequestCustomQuote: () => void;
  selectedTier?: string;
  onTierSelect?: (tierId: string) => void;
}) {
  const isRecurring = offering.pricing.type === "RECURRING";
  const { depositConfig, pricing } = offering;
  const hasRange = !!pricing.priceRange;

  if (isRecurring) {
    return (
      <div className="mt-4 space-y-4">
        <div className="rounded-lg border border-border/50 bg-card/50 p-4">
          <div className="flex items-center gap-2 mb-3">
            <CircleDollarSign className="h-4 w-4 text-primary" />
            <p className="text-sm font-medium">Pricing & Setup</p>
          </div>
          {depositConfig.setupFee && (
            <div className="flex items-center justify-between text-sm mb-3 pb-3 border-b border-border/30">
              <span className="text-muted-foreground">One-time setup fee</span>
              <span className="font-semibold text-primary">
                {formatCurrency(depositConfig.setupFee)}
              </span>
            </div>
          )}
          {pricing.retainerTiers && pricing.retainerTiers.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground mb-1">
                Select a tier
              </p>
              {pricing.retainerTiers.map((tier) => {
                const tierId = tier.name.toLowerCase().replace(/^the\s+/, "");
                const isSelected = selectedTier === tierId;
                return (
                  <button
                    key={tier.name}
                    type="button"
                    onClick={() => onTierSelect?.(tierId)}
                    className={cn(
                      "w-full flex items-center justify-between text-sm px-3 py-2.5 rounded-md border transition-all text-left",
                      isSelected
                        ? "border-primary bg-primary/10"
                        : "border-white/5 bg-white/5 hover:border-primary/40",
                    )}
                  >
                    <div className="flex items-center gap-2">
                      {isSelected && (
                        <Check className="h-3.5 w-3.5 text-primary shrink-0" />
                      )}
                      <div>
                        <p className="font-medium text-foreground">{tier.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {tier.responseTime} response &middot;{" "}
                          {tier.internalHourCap}h/mo
                        </p>
                      </div>
                    </div>
                    <span className="text-primary font-semibold whitespace-nowrap ml-3">
                      {formatCurrency(tier.monthlyPrice)}/mo
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <Button
          variant="ghost"
          size="sm"
          onClick={onRequestCustomQuote}
          className="w-full text-muted-foreground hover:text-foreground"
        >
          <MessageSquare className="h-4 w-4 mr-2" />
          Request Custom Quote
        </Button>
      </div>
    );
  }

  return (
    <div className="mt-4 space-y-4">
      <div className="rounded-lg border border-border/50 bg-card/50 p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Milestone className="h-4 w-4 text-primary" />
            <p className="text-sm font-medium">Milestone Breakdown</p>
          </div>
          {hasRange && (
            <div className="text-right">
              <p className="text-xs text-muted-foreground">Total range</p>
              <p className="text-sm font-semibold text-primary">
                {formatCurrency(pricing.priceRange!.min)} –{" "}
                {formatCurrency(pricing.priceRange!.max)}
              </p>
            </div>
          )}
        </div>

        <div className="space-y-0">
          {depositConfig.milestones.map((milestone, idx) => {
            const isFirst = idx === 0;
            const isLast = idx === depositConfig.milestones.length - 1;
            const totalMilestones = depositConfig.milestones.length;

            let estimatedShare: string;
            if (isFirst && depositConfig.depositPercentage > 0) {
              estimatedShare = `${depositConfig.depositPercentage}%`;
            } else {
              const remaining = 100 - depositConfig.depositPercentage;
              const otherCount = totalMilestones - 1;
              estimatedShare = `${Math.round(remaining / otherCount)}%`;
            }

            return (
              <div key={milestone.order} className="relative flex gap-3">
                <div className="flex flex-col items-center">
                  <div
                    className={cn(
                      "w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 z-10",
                      isFirst
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground",
                    )}
                  >
                    {milestone.order}
                  </div>
                  {!isLast && (
                    <div className="w-px flex-1 bg-border/50 min-h-[24px]" />
                  )}
                </div>
                <div className={cn("pb-4 flex-1 min-w-0", isLast && "pb-0")}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium leading-tight">
                        {milestone.label}
                      </p>
                      {milestone.depositRequired && (
                        <Badge
                          variant="outline"
                          className="mt-1.5 text-[10px] border-primary/40 text-primary"
                        >
                          {depositConfig.depositPercentage}% deposit required
                          before work begins
                        </Badge>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">
                      ~{estimatedShare}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {depositConfig.required && hasRange && (
          <div className="mt-4 pt-3 border-t border-border/30">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                Deposit on Milestone 1 ({depositConfig.depositPercentage}%)
              </span>
              <span className="font-semibold text-primary">
                {formatCurrency(
                  pricing.priceRange!.min *
                    (depositConfig.depositPercentage / 100),
                )}{" "}
                –{" "}
                {formatCurrency(
                  pricing.priceRange!.max *
                    (depositConfig.depositPercentage / 100),
                )}
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Remaining milestones paid on completion. Final scope and pricing
              confirmed after discovery call.
            </p>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 text-sm text-muted-foreground px-1">
        <Clock className="h-4 w-4 shrink-0" />
        <span>{offering.timelineEstimate} estimated delivery</span>
      </div>

      <Button
        variant="ghost"
        size="sm"
        onClick={onRequestCustomQuote}
        className="w-full text-muted-foreground hover:text-foreground"
      >
        <MessageSquare className="h-4 w-4 mr-2" />
        Request Custom Quote
      </Button>
    </div>
  );
}

function EmailStatusIndicator({ status }: { status: EmailDomainStatus }) {
  if (status === "verifying") {
    return (
      <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5 pointer-events-none">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground/60" />
        <span className="text-xs text-muted-foreground/60">Verifying</span>
      </div>
    );
  }
  if (status === "valid") {
    return (
      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
        <CheckCircle2 className="h-4 w-4 text-primary/80" />
      </div>
    );
  }
  if (
    status === "disposable" ||
    status === "personal" ||
    status === "error"
  ) {
    return (
      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
        <XCircle className="h-4 w-4 text-destructive" />
      </div>
    );
  }
  return null;
}

function emailInputClassName(
  status: EmailDomainStatus,
  hasError: boolean,
): string {
  const base = "pr-28";
  if (hasError) return `${base} border-destructive/50`;
  if (status === "valid") return `${base} border-primary/30`;
  if (status === "verifying") return `${base} border-muted-foreground/20`;
  return base;
}

interface ProjectBasicsStepProps {
  data: ProjectBasicsData;
  onChange: (data: ProjectBasicsData) => void;
  onNext: () => void;
  preselectedPackageId?: string;
}

export function ProjectBasicsStep({
  data,
  onChange,
  onNext,
  preselectedPackageId,
}: ProjectBasicsStepProps) {
  const { offerings } = usePackageOfferings();
  const { toast } = useToast();
  const [expandedBreakdown, setExpandedBreakdown] = useState<string | null>(
    preselectedPackageId || null,
  );
  const isRecurring = isRecurringPackage(data.selectedPackageId);

  const {
    status: emailStatus,
    errorMessage: emailError,
    isBlocking: emailIsBlocking,
    validateEmail,
    sanitizeEmail,
  } = useEmailDomainValidation();

  const turnstileVerified = useRef(false);

  useEffect(() => {
    if (data.email) {
      validateEmail(data.email);
    }
  }, []);

  useEffect(() => {
    if (preselectedPackageId && !data.selectedPackageId) {
      onChange({ ...data, selectedPackageId: preselectedPackageId });
      setExpandedBreakdown(preselectedPackageId);
    }
  }, [preselectedPackageId]);

  const handleFieldChange = (field: keyof ProjectBasicsData, value: string) => {
    onChange({ ...data, [field]: value });
  };

  const handleEmailChange = (value: string) => {
    const sanitized = sanitizeEmail(value);
    onChange({ ...data, email: sanitized });
    validateEmail(sanitized);
  };

  const handlePackageSelect = (packageId: string) => {
    onChange({ ...data, selectedPackageId: packageId });
  };

  const toggleBreakdown = (packageId: string) => {
    setExpandedBreakdown((prev) => (prev === packageId ? null : packageId));
  };

  const handleRequestCustomQuote = () => {
    toast({
      title: "Coming soon",
      description:
        "Custom quoting will be available soon. For now, proceed with the standard package and we'll discuss scope on the discovery call.",
    });
  };

  const isValid =
    data.name.trim() !== "" &&
    data.companyName.trim() !== "" &&
    !emailIsBlocking &&
    data.industryId !== "" &&
    data.companySize !== "" &&
    data.timezone !== "" &&
    data.selectedPackageId !== null &&
    (isRecurring
      ? (data.currentSituation?.trim() || "") !== "" &&
        (data.improvementGoals?.trim() || "") !== ""
      : data.projectSummary.trim() !== "");

  return (
    <Card className="glass-card border-gradient">
      <CardContent className="p-6 md:p-8">
        <h2 className="font-display text-2xl font-bold mb-6">Project Basics</h2>

        <div className="space-y-6">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                placeholder="John Smith"
                value={data.name}
                onChange={(e) => handleFieldChange("name", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="company">Company Name *</Label>
              <Input
                id="company"
                placeholder="Acme Inc"
                value={data.companyName}
                onChange={(e) =>
                  handleFieldChange("companyName", e.target.value)
                }
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Business Email *</Label>
            <div className="relative">
              <Input
                id="email"
                type="text"
                autoComplete="off"
                placeholder="john@acme.com"
                value={data.email}
                onChange={(e) => handleEmailChange(e.target.value)}
                className={emailInputClassName(emailStatus, !!emailError)}
              />
              <EmailStatusIndicator status={emailStatus} />
            </div>
            {emailError && (
              <p className="text-sm text-destructive">{emailError}</p>
            )}
          </div>

          <div className="space-y-3">
            <Label>Package *</Label>
            <div className="space-y-3">
              {offerings.map((offering: PackageOfferingDto) => {
                const Icon = getIconForPackage(offering.id);
                const isSelected = data.selectedPackageId === offering.id;
                const isBreakdownOpen = expandedBreakdown === offering.id;
                const offeringIsRecurring =
                  offering.pricing.type === "RECURRING";

                return (
                  <div key={offering.id}>
                    <button
                      type="button"
                      onClick={() => {
                        handlePackageSelect(offering.id);
                        toggleBreakdown(offering.id);
                      }}
                      className={cn(
                        "w-full rounded-lg border transition-all text-left p-4",
                        isSelected
                          ? "border-primary bg-primary/5"
                          : "border-border hover:border-primary/30",
                        isBreakdownOpen && "rounded-b-none",
                      )}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className={cn(
                            "w-10 h-10 rounded-lg flex items-center justify-center shrink-0",
                            isSelected ? "bg-primary/20" : "bg-primary/10",
                          )}
                        >
                          {isSelected ? (
                            <Check className="h-5 w-5 text-primary" />
                          ) : (
                            <Icon className="h-5 w-5 text-primary" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <p className="font-medium truncate">
                              {offering.name}
                            </p>
                            <div className="flex items-center gap-2 shrink-0">
                              {offering.metadata.isPopular && (
                                <Badge className="bg-gradient-to-r from-primary to-accent text-primary-foreground text-[10px]">
                                  Popular
                                </Badge>
                              )}
                              <Badge variant="secondary" className="text-xs">
                                {offeringIsRecurring
                                  ? "Recurring"
                                  : offering.timelineEstimate}
                              </Badge>
                              <ChevronDown
                                className={cn(
                                  "h-4 w-4 text-muted-foreground transition-transform",
                                  isBreakdownOpen && "rotate-180",
                                )}
                              />
                            </div>
                          </div>
                          <p className="text-sm text-muted-foreground line-clamp-1 mt-1">
                            {offering.tagline}
                          </p>
                          {offering.pricing.priceRange && (
                            <p className="text-sm font-semibold text-primary mt-1">
                              {formatCurrency(offering.pricing.priceRange.min)}
                              {offeringIsRecurring ? "/mo" : ""} –{" "}
                              {formatCurrency(offering.pricing.priceRange.max)}
                              {offeringIsRecurring ? "/mo" : ""}
                            </p>
                          )}
                        </div>
                      </div>
                    </button>

                    <AnimatePresence>
                      {isBreakdownOpen && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.2 }}
                          className={cn(
                            "overflow-hidden border border-t-0 rounded-b-lg px-4 pb-4",
                            isSelected
                              ? "border-primary bg-primary/5"
                              : "border-border bg-muted/30",
                          )}
                        >
                          <PackageMilestoneBreakdown
                            offering={offering}
                            onRequestCustomQuote={handleRequestCustomQuote}
                            selectedTier={data.tierIntent}
                            onTierSelect={(tierId) =>
                              onChange({
                                ...data,
                                tierIntent: tierId as ProjectBasicsData["tierIntent"],
                              })
                            }
                          />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </div>

          {isRecurring ? (
            <>
              <div className="space-y-2">
                <Label htmlFor="currentSituation">Current Situation *</Label>
                <Textarea
                  id="currentSituation"
                  placeholder="Describe your current desktop app, release pipeline, and what's working or not..."
                  rows={2}
                  value={data.currentSituation || ""}
                  onChange={(e) =>
                    handleFieldChange("currentSituation", e.target.value)
                  }
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="improvementGoals">
                  What do you want improved? *
                </Label>
                <Textarea
                  id="improvementGoals"
                  placeholder="Release cadence, auto-update, code signing, crash reporting..."
                  rows={2}
                  value={data.improvementGoals || ""}
                  onChange={(e) =>
                    handleFieldChange("improvementGoals", e.target.value)
                  }
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Target Platform(s)</Label>
                  <Input
                    placeholder="macOS, Windows, Linux..."
                    value={data.cloudProviders || ""}
                    onChange={(e) =>
                      handleFieldChange("cloudProviders", e.target.value)
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>Environment Count</Label>
                  <Select
                    value={data.environmentCount || ""}
                    onValueChange={(v) =>
                      handleFieldChange("environmentCount", v)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="How many environments?" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 (Production only)</SelectItem>
                      <SelectItem value="2">2 (Dev + Prod)</SelectItem>
                      <SelectItem value="3">3 (Dev + Stage + Prod)</SelectItem>
                      <SelectItem value="4+">4+ environments</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Monitoring/Alerts Exist?</Label>
                <Select
                  value={data.hasMonitoring || ""}
                  onValueChange={(v) => handleFieldChange("hasMonitoring", v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="partial">Partially</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="summary">One-line Project Summary *</Label>
              <Textarea
                id="summary"
                placeholder="Brief description of what you want to build..."
                rows={2}
                value={data.projectSummary}
                onChange={(e) =>
                  handleFieldChange("projectSummary", e.target.value)
                }
              />
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Industry *</Label>
              <Select
                value={data.industryId}
                onValueChange={(v) => handleFieldChange("industryId", v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select industry" />
                </SelectTrigger>
                <SelectContent>
                  {INDUSTRIES.map((industry) => (
                    <SelectItem key={industry.id} value={industry.id}>
                      {industry.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Company Size *</Label>
              <Select
                value={data.companySize}
                onValueChange={(v) => handleFieldChange("companySize", v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select size" />
                </SelectTrigger>
                <SelectContent>
                  {COMPANY_SIZES.map((size) => (
                    <SelectItem key={size} value={size}>
                      {size} employees
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Time Zone *</Label>
            <Select
              value={data.timezone}
              onValueChange={(v) => handleFieldChange("timezone", v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select your time zone" />
              </SelectTrigger>
              <SelectContent>
                {TIMEZONES.map((tz) => (
                  <SelectItem key={tz.id} value={tz.id}>
                    {tz.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="sr-only" aria-hidden="true">
            <CloudflareTurnstile
              onVerify={() => {
                turnstileVerified.current = true;
              }}
              size="compact"
            />
          </div>

          <div className="pt-4 flex justify-end">
            <Button onClick={onNext} disabled={!isValid} size="lg">
              Next
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
